import React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import {
  Settings,
  Users,
  BarChart4,
  ClipboardList,
  CreditCard,
  Shield,
  CalendarDays,
  Image,
  DollarSign,
  UserCog,
  Globe,
} from "lucide-react";

const AdminSettings = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const settingsSections = [
    {
      title: "Company & Organization Settings",
      description: "Update global branding, contact info, system preferences, and basic policies.",
      icon: <Settings />,
      link: "/admin/settings/company",
    },
    {
      title: "Convention Subdomain",
      description: "Register and manage your convention's branded subdomain (e.g., yourconvention.dance.digital).",
      icon: <Globe />,
      link: "/admin/settings/convention-subdomain",
    },
    {
      title: "User & Staff Management",
      description: "Create and manage admin/staff accounts, control permissions, and roles.",
      icon: <Users />,
      link: "/admin/settings/users",
    },
    {
      title: "Season & Event Management",
      description: "Create new seasons, configure events, handle capacity limits, and deposits.",
      icon: <CalendarDays />,
      link: "/admin/settings/season-events",
    },
    {
      title: "Age Divisions & Categories",
      description: "Define age groups, determination methods, and style categories for competitions.",
      icon: <ClipboardList />,
      link: "/admin/settings/registration-rules",
    },
    {
      title: "Pricing Settings",
      description: "Manage registration and competition entry pricing, late fees, and discount rates.",
      icon: <DollarSign />,
      link: "/admin/settings/pricing",
    },
    {
      title: "Scheduling & Performance Order",
      description: "Manage block scheduling, session times, conflict minimization, and breaks.",
      icon: <BarChart4 />,
      link: "/admin/settings/scheduling",
    },
    {
      title: "Judge Management",
      description: "Create and manage judge profiles, scoring criteria, awards, and station settings for competitions.",
      icon: <UserCog />,
      link: "/admin/settings/judging-awards",
    },
    {
      title: "Payment & Financial Settings",
      description: "Configure Stripe/PayPal, payment methods, refunds, deposit rules, and fees.",
      icon: <CreditCard />,
      link: "/admin/settings/payment-financial",
    },
    {
      title: "Media & Uploads",
      description: "Control music, photo/video uploads, over-time fees, and critique storage.",
      icon: <Image />,
      link: "/admin/settings/media",
    },
    {
      title: "Reporting & Analytics",
      description: "Customize or generate event reports, financial exports, and performance dashboards.",
      icon: <BarChart4 />,
      link: "/admin/settings/reports",
    },
    {
      title: "Security & Access Control",
      description: "Set password policies, 2FA, role-based restrictions, and audit trails.",
      icon: <Shield />,
      link: "/admin/settings/security",
    },
  ];

  const SettingsCard = ({ title, description, icon, link }) => (
    <Link
      to={link}
      className={`card shadow-xl hover:shadow-2xl transition-all duration-300 group ${
        isDarkMode 
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
          : 'bg-base-100 hover:bg-base-200'
      }`}
      aria-labelledby={`section-${title}-title`}
    >
      <div className="card-body p-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg transition-colors ${
            isDarkMode 
              ? 'bg-primary/20 group-hover:bg-primary/30' 
              : 'bg-primary/10 group-hover:bg-primary/20'
          }`}>
            {React.cloneElement(icon, { className: "w-6 h-6 text-primary" })}
          </div>
          <div>
            <h2
              id={`section-${title}-title`}
              className="card-title text-lg font-semibold group-hover:text-primary transition-colors"
            >
              {title}
            </h2>
            <p className="text-base-content/60 text-sm mt-1">{description}</p>
          </div>
        </div>
        <div className="card-actions justify-end mt-4">
          <button
            className="btn btn-sm btn-ghost group-hover:btn-primary transition-all duration-300"
            aria-label={`Configure ${title}`}
          >
            Configure
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-4 h-4 ml-1"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        </div>
      </div>
    </Link>
  );

  return (
    <div className="container mx-auto p-6 space-y-8">
      {/* Header */}
      <div className={`rounded-box p-6 shadow-xl ${
        isDarkMode 
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
          : 'bg-base-100'
      }`}>
        <div>
          <h1 className="text-4xl font-bold text-base-content">Admin Settings</h1>
          <p className="text-base-content/60 mt-2">
            Configure and manage all aspects of your convention system
          </p>
        </div>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {settingsSections.map((section) => (
          <SettingsCard key={section.title} {...section} />
        ))}
      </div>

      {/* Quick Tips */}
      <div className={`card shadow-xl ${
        isDarkMode 
          ? 'bg-[#1a1f2d]/80 backdrop-blur-sm border border-white/5' 
          : 'bg-base-100'
      }`}>
        <div className="card-body">
          <div className="flex items-center gap-2 text-primary mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
              />
            </svg>
            <h3 className="font-semibold text-base-content">Quick Setup Tips</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span className="text-base-content/80">
                Configure basic "Company & Organization" info first
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span className="text-base-content/80">
                Set up "Season & Event" details for capacity rules
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-primary/60" />
              <span className="text-base-content/80">
                Connect your payment gateway in "Payment & Financial Settings"
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
